/**
 * @description
 * Custom hook for managing marketplace configuration data
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '../../../services/api';
import { UseMarketplaceConfigReturn, MarketplaceSystemConfig } from '../types';

/**
 * Custom hook for fetching and managing marketplace configuration data
 */
export function useMarketplaceConfig(): UseMarketplaceConfigReturn {
  const [config, setConfig] = useState<MarketplaceSystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deploymentTestResult, setDeploymentTestResult] = useState<any>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await api.getMarketplaceConfig();
      setConfig(configData.data);
    } catch (error) {
      console.error('Error fetching marketplace config:', error);
      setError(error instanceof Error ? error.message : 'Failed to load marketplace configuration');
      toast.error('Failed to load marketplace configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (data: Partial<MarketplaceSystemConfig>) => {
    try {
      const updatedConfig = await api.updateMarketplaceConfig(data);
      setConfig(updatedConfig.data);
      toast.success('Marketplace configuration updated successfully');
    } catch (error) {
      console.error('Error updating marketplace config:', error);
      toast.error('Failed to update marketplace configuration');
      throw error;
    }
  };

  const testDeployment = async () => {
    try {
      const result = await api.testMarketplaceDeployment();
      setDeploymentTestResult(result);
      toast.success('Deployment test completed');
    } catch (error) {
      console.error('Error testing deployment:', error);
      toast.error('Failed to test deployment');
      throw error;
    }
  };

  const refetch = useCallback(async () => {
    await fetchConfig();
  }, []);

  useEffect(() => {
    fetchConfig();
  }, []);

  return {
    config,
    loading,
    error,
    refetch,
    updateConfig,
    testDeployment,
    deploymentTestResult,
  };
}